import type { DataProfile, ChartRecommendation, ChartConfig } from '../../types';

/**
 * Decision waterfall that selects the best chart type from a DataProfile.
 * Rules are evaluated top-to-bottom; the first match wins.
 */
export function selectChartType(profile: DataProfile): ChartRecommendation {
  const { numericCount, discreteCount, dateCount, rowCount, columns, isFinancial } = profile;

  const numericCols = columns.filter((c) => c.type === 'numeric');
  const discreteCols = columns.filter((c) => c.type === 'discrete');
  const dateCols = columns.filter((c) => c.type === 'date');

  // Helper to build config with axis assignments
  const cfg = (overrides: Partial<ChartConfig> = {}): ChartConfig => ({
    xAxis: dateCols[0]?.name ?? discreteCols[0]?.name ?? numericCols[0]?.name,
    yAxis: numericCols.length === 1
      ? numericCols[0]?.name
      : numericCols.map((c) => c.name),
    ...overrides,
  });

  // ── Rule 0: KPI Card — single-row scalar results ──────────────
  // When ERAX returns a single row with numeric value(s), no chart
  // makes sense (a donut with 1 slice, a bar with 1 bar, etc.).
  // Show a prominent KPI metric card instead.
  //
  // 0a: Single row, only numeric columns (e.g. { TotalRepairCost: 10407451.24 })
  // 0b: Single row, discrete label(s) + numeric values (e.g. { Port: "SG", TEU: 5000 })
  //     The discrete columns become card subtitles/labels.
  if (rowCount === 1 && numericCount >= 1 && dateCount === 0) {
    const rule = discreteCount === 0 ? '0a' : '0b';
    return {
      chartType: 'kpi_card',
      matchedRule: rule,
      logic: `Single row with ${numericCount} numeric value(s) and ${discreteCount} label(s) → KPI card`,
      config: cfg({
        yAxis: numericCols.map((c) => c.name),
        xAxis: discreteCols[0]?.name,
      }),
    };
  }

  // ── Rule 1: Financial Override ──────────────────────────────────

  // 1a: OHLC + Date → Candlestick
  if (isFinancial && dateCount === 1) {
    const lower = profile.columnNames;
    const hasOHLC = ['open', 'high', 'low', 'close'].every((c) =>
      lower.some((n) => n === c || n.includes(c)),
    );
    if (hasOHLC) {
      return {
        chartType: 'candlestick',
        matchedRule: '1a',
        logic: 'Financial OHLC columns detected with a date axis',
        config: cfg({
          xAxis: dateCols[0]?.name,
          yAxis: ['open', 'high', 'low', 'close'].map(
            (target) => columns.find((c) => c.name.toLowerCase().includes(target))?.name ?? target,
          ),
        }),
      };
    }

    // 1b: Price/Volume + Date → Area + Volume Sub-pane
    const priceCols = numericCols.filter(
      (c) => !c.name.toLowerCase().includes('volume') && !c.name.toLowerCase().includes('vol'),
    );
    const volumeCol = numericCols.find(
      (c) => c.name.toLowerCase().includes('volume') || c.name.toLowerCase().includes('vol'),
    );
    if (priceCols.length >= 1 && volumeCol) {
      return {
        chartType: 'area_volume',
        matchedRule: '1b',
        logic: 'Price and volume columns detected with a date axis',
        config: cfg({
          xAxis: dateCols[0]?.name,
          yAxis: priceCols[0]?.name,
          sizeAxis: volumeCol.name,
        }),
      };
    }
  }

  // ── Rule 2: Time-Series ─────────────────────────────────────────

  // 2a: Date + 1 Numeric → Line Chart
  if (dateCount === 1 && numericCount === 1) {
    return {
      chartType: 'line',
      matchedRule: '2a',
      logic: 'One date column and one numeric column → time-series line',
      config: cfg({ xAxis: dateCols[0]?.name, yAxis: numericCols[0]?.name }),
    };
  }

  // 2b: Date + 2+ Numeric → Multi-Line
  if (dateCount === 1 && numericCount >= 2) {
    return {
      chartType: 'multi_line',
      matchedRule: '2b',
      logic: `One date column and ${numericCount} numeric columns → multi-line`,
      config: cfg({
        xAxis: dateCols[0]?.name,
        yAxis: numericCols.map((c) => c.name),
      }),
    };
  }

  // 2c: 2+ date-like columns (e.g. Year + Month) + 1 numeric → Line (use first date as x)
  if (dateCount >= 2 && numericCount === 1) {
    return {
      chartType: 'line',
      matchedRule: '2c',
      logic: 'Multiple date-like columns (e.g. Year, Month) and one numeric → time-series line',
      config: cfg({ xAxis: dateCols[0]?.name, yAxis: numericCols[0]?.name }),
    };
  }

  // 2d: 2+ date-like columns + 2+ numeric → Multi-Line
  //     e.g. Year, Month, Revenue, TEU
  if (dateCount >= 2 && numericCount >= 2) {
    return {
      chartType: 'multi_line',
      matchedRule: '2d',
      logic: `Multiple date-like columns and ${numericCount} numeric columns → multi-line`,
      config: cfg({
        xAxis: dateCols[0]?.name,
        yAxis: numericCols.map((c) => c.name),
      }),
    };
  }

  // ── Rule 3: Comparison ──────────────────────────────────────────

  // 3a: Discrete=1 AND Numeric=1 AND cardinality ≤ 5 → Donut
  if (discreteCount === 1 && numericCount === 1) {
    const card = discreteCols[0]?.cardinality ?? 0;
    if (card <= 5) {
      return {
        chartType: 'donut',
        matchedRule: '3a',
        logic: `One discrete column (cardinality ${card} ≤ 5) and one numeric → donut`,
        config: cfg({ xAxis: discreteCols[0]?.name, yAxis: numericCols[0]?.name }),
      };
    }

    // 3b: Discrete=1 AND Numeric=1 AND cardinality > 5 → Bar
    return {
      chartType: 'bar',
      matchedRule: '3b',
      logic: `One discrete column (cardinality ${card} > 5) and one numeric → bar chart`,
      config: cfg({
        xAxis: discreteCols[0]?.name,
        yAxis: numericCols[0]?.name,
        horizontal: false,
        sorted: true,
      }),
    };
  }

  // 3c: Discrete=1 AND Numeric=2+ → Grouped Bar (side-by-side bars per metric)
  //     e.g. "Revenue and TEU by port" → Port on x-axis, Revenue + TEU as separate bars
  if (discreteCount === 1 && numericCount >= 2) {
    return {
      chartType: 'bar',
      matchedRule: '3c',
      logic: `One discrete column and ${numericCount} numeric columns → grouped bar`,
      config: cfg({
        xAxis: discreteCols[0]?.name,
        yAxis: numericCols.map((c) => c.name),
        grouped: true,
      }),
    };
  }

  // 3d: Discrete=2 AND Numeric=1 → Stacked Bar
  if (discreteCount === 2 && numericCount === 1) {
    return {
      chartType: 'stacked_bar',
      matchedRule: '3d',
      logic: 'Two discrete columns and one numeric → stacked bar',
      config: cfg({
        xAxis: discreteCols[0]?.name,
        yAxis: numericCols[0]?.name,
        groupBy: discreteCols[1]?.name,
      }),
    };
  }

  // ── Rule 4: Correlation / Relationship ──────────────────────────

  // 4a: Numeric=2, no discrete, no date → Scatter
  if (numericCount === 2 && discreteCount === 0 && dateCount === 0) {
    return {
      chartType: 'scatter',
      matchedRule: '4a',
      logic: 'Two numeric columns with no categories → scatter plot',
      config: cfg({
        xAxis: numericCols[0]?.name,
        yAxis: numericCols[1]?.name,
      }),
    };
  }

  // 4b: Numeric=3, no discrete, no date → Bubble
  if (numericCount === 3 && discreteCount === 0 && dateCount === 0) {
    return {
      chartType: 'bubble',
      matchedRule: '4b',
      logic: 'Three numeric columns → bubble chart (third as size)',
      config: cfg({
        xAxis: numericCols[0]?.name,
        yAxis: numericCols[1]?.name,
        sizeAxis: numericCols[2]?.name,
      }),
    };
  }

  // 4c: Numeric ≥ 4, no discrete, no date → Table (too many axes for a meaningful chart)
  //     Falls through to Rule 6 naturally, but documenting for completeness.

  // ── Rule 5: Distribution ────────────────────────────────────────

  // 5a: Numeric=1, no others, rows > 50 → Histogram
  if (numericCount === 1 && discreteCount === 0 && dateCount === 0 && rowCount > 50) {
    return {
      chartType: 'histogram',
      matchedRule: '5a',
      logic: `Single numeric column with ${rowCount} rows (> 50) → histogram`,
      config: cfg({ xAxis: numericCols[0]?.name }),
    };
  }

  // 5b: Numeric=1, no others, rows ≤ 50 → Box Plot
  if (numericCount === 1 && discreteCount === 0 && dateCount === 0 && rowCount <= 50) {
    return {
      chartType: 'box_plot',
      matchedRule: '5b',
      logic: `Single numeric column with ${rowCount} rows (≤ 50) → box plot`,
      config: cfg({ xAxis: numericCols[0]?.name }),
    };
  }

  // ── Rule 6: Fallback ───────────────────────────────────────────

  return {
    chartType: 'table',
    matchedRule: '6',
    logic: `No waterfall rule matched (numeric=${numericCount}, discrete=${discreteCount}, date=${dateCount})`,
    config: {},
  };
}
